/**
 * ReferralCampaignTab - Admin dashboard for referral program reporting
 * Shows stats, coupon tracking, SMS campaign controls, and referral activity
 */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Users, Gift, TrendingUp, Send, Clock, CheckCircle,
  AlertTriangle, RefreshCw, ChevronRight, BarChart3,
  MessageSquare, Percent, Trophy, MousePointer, UserPlus,
  Rocket, Eye, Mail, Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function ReferralCampaignTab({ authHeaders }) {
  const [stats, setStats] = useState(null);
  const [couponReport, setCouponReport] = useState(null);
  const [smsQueue, setSmsQueue] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [couponFilter, setCouponFilter] = useState('all');
  const [activeSection, setActiveSection] = useState('overview');
  
  // Launch campaign state
  const [launchPreview, setLaunchPreview] = useState(null);
  const [loadingLaunchPreview, setLoadingLaunchPreview] = useState(false);
  const [sendingLaunch, setSendingLaunch] = useState(false);
  
  // Campaign Growth Dashboard state
  const [growthData, setGrowthData] = useState(null);
  const [loadingGrowth, setLoadingGrowth] = useState(false);
  const [showGrowthDashboard, setShowGrowthDashboard] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, couponsRes, smsRes, activityRes] = await Promise.all([
        api.get('/referral-program/admin/stats'),
        api.get(`/referral-program/admin/coupon-report?status=${couponFilter}&limit=50`),
        api.get('/referral-program/admin/sms-queue'),
        api.get('/referral-tracking/admin/activity')
      ]);

      setStats(statsRes.data);
      setCouponReport(couponsRes.data);
      setSmsQueue(smsRes.data);
      setActivity(activityRes.data);
    } catch (err) {
      console.error('Failed to load referral data:', err);
      toast.error('Failed to load referral program data');
    }
    setLoading(false);
  }, [couponFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Load launch campaign preview
  const loadLaunchPreview = async () => {
    setLoadingLaunchPreview(true);
    try {
      const res = await api.get('/referral-program/admin/launch-campaign-preview');
      setLaunchPreview(res.data);
    } catch (err) {
      toast.error('Failed to load campaign preview');
    }
    setLoadingLaunchPreview(false);
  };
  
  // Send launch campaign
  const sendLaunchCampaign = async (testMode = true) => {
    setSendingLaunch(true);
    try {
      const res = await api.post('/referral-program/admin/send-launch-campaign', {
        test_mode: testMode,
        send_sms: true,
        send_email: true
      });
      
      if (testMode) {
        toast.success(`Preview: ${res.data.total_recipients} members would receive the campaign`);
        setLaunchPreview(prev => ({ ...prev, messages_preview: res.data.messages_preview }));
      } else {
        toast.success(`🚀 Campaign Sent!`, {
          description: `SMS: ${res.data.sms_sent} sent, ${res.data.sms_failed} failed | Email: ${res.data.email_sent} sent`
        });
        loadLaunchPreview(); // Refresh to show remaining
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send campaign');
    }
    setSendingLaunch(false);
  };
  
  // Load campaign growth dashboard
  const loadGrowthData = async () => {
    setLoadingGrowth(true);
    try {
      const res = await api.get('/referral-program/admin/campaign-growth');
      setGrowthData(res.data);
      setShowGrowthDashboard(true);
    } catch (err) {
      toast.error('Failed to load campaign growth data');
    }
    setLoadingGrowth(false);
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSendCampaign = async (campaignType, testMode = true) => {
    setSendingCampaign(true);
    try {
      const res = await api.post('/referral-program/admin/send-sms-campaign', {
        campaign_type: campaignType,
        test_mode: testMode
      });

      if (testMode) {
        toast.success(`Preview: ${res.data.total_to_send} messages would be sent`, {
          description: 'Review the messages below before sending'
        });
      } else {
        toast.success(`Campaign sent!`, {
          description: `${res.data.sent} messages sent, ${res.data.failed} failed`
        });
        // Refresh data
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process campaign');
    }
    setSendingCampaign(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Referral Campaign</h2>
          <p className="text-muted-foreground">Track referrals, coupons, and SMS campaigns</p>
        </div>
        <Button onClick={loadData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* 🚀 LAUNCH REFERRAL CAMPAIGN - Primary Action */}
      <Card className="border-2 border-green-500 dark:border-green-600 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-green-700 dark:text-green-400">
            <Rocket className="w-6 h-6" />
            Launch Referral Campaign
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            Send the referral program announcement to all your Founding Members. They&apos;ll receive an SMS and email with instructions on how to login, find their referral code, and start sharing.
          </p>
          
          {!launchPreview ? (
            <Button 
              onClick={loadLaunchPreview} 
              disabled={loadingLaunchPreview}
              size="lg"
              className="bg-green-600 hover:bg-green-700"
            >
              {loadingLaunchPreview ? (
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Eye className="w-5 h-5 mr-2" />
              )}
              Preview Campaign
            </Button>
          ) : (
            <div className="space-y-4">
              {/* Recipients Count */}
              <div className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg border">
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{launchPreview.total_recipients}</p>
                  <p className="text-sm text-muted-foreground">Members will receive this campaign</p>
                </div>
              </div>
              
              {/* SMS Preview */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Phone className="w-5 h-5 text-blue-500" />
                  <h4 className="font-semibold">SMS Message</h4>
                  <span className="text-xs text-muted-foreground">({launchPreview.sms_char_count} characters)</span>
                </div>
                <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap font-mono">
                  {launchPreview.sms_message}
                </pre>
              </div>
              
              {/* Email Preview */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-5 h-5 text-purple-500" />
                  <h4 className="font-semibold">Email</h4>
                </div>
                <p className="text-sm"><strong>Subject:</strong> {launchPreview.email_subject}</p>
                <p className="text-sm text-muted-foreground mt-1">{launchPreview.email_description}</p>
              </div>
              
              {/* Login Flow Note */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-blue-700 dark:text-blue-400 text-sm">
                  <strong>📱 Login Flow:</strong> {launchPreview.note}
                </p>
              </div>
              
              {/* Sample Recipients */}
              {launchPreview.sample_recipients?.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border p-4">
                  <h4 className="font-semibold mb-2">Sample Recipients (first 10)</h4>
                  <div className="space-y-1 text-sm">
                    {launchPreview.sample_recipients.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 text-muted-foreground">
                        <span>{r.name}</span>
                        <span>•</span>
                        <span>{r.phone || 'No phone'}</span>
                        <span>•</span>
                        <span>{r.email || 'No email'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setLaunchPreview(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => sendLaunchCampaign(true)}
                  disabled={sendingLaunch}
                  variant="outline"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Test Preview (Don&apos;t Send)
                </Button>
                <Button
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to send this campaign to ${launchPreview.total_recipients} members? This will send both SMS and Email.`)) {
                      sendLaunchCampaign(false);
                    }
                  }}
                  disabled={sendingLaunch || launchPreview.total_recipients === 0}
                  className="bg-green-600 hover:bg-green-700"
                  size="lg"
                >
                  {sendingLaunch ? (
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  Send Campaign to {launchPreview.total_recipients} Members
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 📊 Campaign Growth Dashboard */}
      <Card className="border-2 border-blue-500 dark:border-blue-600">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl flex items-center gap-2 text-blue-700 dark:text-blue-400">
              <BarChart3 className="w-6 h-6" />
              Campaign Growth Dashboard
            </CardTitle>
            <Button 
              onClick={loadGrowthData} 
              disabled={loadingGrowth}
              variant={showGrowthDashboard ? "outline" : "default"}
              className={showGrowthDashboard ? "" : "bg-blue-600 hover:bg-blue-700"}
            >
              {loadingGrowth ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <TrendingUp className="w-4 h-4 mr-2" />
              )}
              {showGrowthDashboard ? "Refresh Data" : "View Growth Data"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!showGrowthDashboard ? (
            <p className="text-muted-foreground">
              Track member growth, referrals, and Coffee Pass purchases since the campaign launch. Click &quot;View Growth Data&quot; to see the latest metrics.
            </p>
          ) : growthData ? (
            <div className="space-y-6">
              {/* Campaign Info */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-blue-700 dark:text-blue-400 text-sm">
                  <strong>Campaign:</strong> {growthData.campaign_info.name}<br/>
                  <strong>Started:</strong> {new Date(growthData.campaign_info.start_date).toLocaleDateString()}<br/>
                  <strong>Baseline Members:</strong> {growthData.campaign_info.baseline_members}
                </p>
              </div>
              
              {/* Member Growth */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-green-500" />
                  Member Growth
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg border p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{growthData.member_growth.current_total}</p>
                    <p className="text-xs text-muted-foreground">Current Total</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg border p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">+{growthData.member_growth.growth}</p>
                    <p className="text-xs text-muted-foreground">Growth ({growthData.member_growth.growth_percentage}%)</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg border p-4 text-center">
                    <p className="text-3xl font-bold text-purple-600">{growthData.member_growth.referred_since_campaign}</p>
                    <p className="text-xs text-muted-foreground">Via Referrals</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg border p-4 text-center">
                    <p className="text-3xl font-bold text-amber-600">{growthData.referral_activity.referrals_since_campaign}</p>
                    <p className="text-xs text-muted-foreground">Successful Referrals</p>
                  </div>
                </div>
              </div>
              
              {/* Coffee Pass Sales */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-amber-500" />
                  Bula Coffee Pass Sales
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg border p-4 text-center">
                    <p className="text-3xl font-bold">{growthData.coffee_pass_sales.since_campaign}</p>
                    <p className="text-xs text-muted-foreground">Sold Since Campaign</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg border p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{growthData.coffee_pass_sales.since_campaign_breakdown.by_existing_members}</p>
                    <p className="text-xs text-muted-foreground">By Existing Members</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg border p-4 text-center">
                    <p className="text-3xl font-bold text-purple-600">{growthData.coffee_pass_sales.since_campaign_breakdown.by_referred_members}</p>
                    <p className="text-xs text-muted-foreground">By Referred Members</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg border p-4 text-center">
                    <p className="text-3xl font-bold text-amber-600">${growthData.coffee_pass_sales.revenue_since_campaign}</p>
                    <p className="text-xs text-muted-foreground">Revenue (FJD)</p>
                  </div>
                </div>
              </div>
              
              {/* Referral Link Performance */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <MousePointer className="w-5 h-5 text-blue-500" />
                  Referral Link Performance
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg border p-4 text-center">
                    <p className="text-2xl font-bold">{growthData.referral_activity.link_clicks.since_campaign}</p>
                    <p className="text-xs text-muted-foreground">Link Clicks</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg border p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{growthData.referral_activity.link_clicks.conversions}</p>
                    <p className="text-xs text-muted-foreground">Conversions</p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg border p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{growthData.referral_activity.link_clicks.conversion_rate}%</p>
                    <p className="text-xs text-muted-foreground">Conversion Rate</p>
                  </div>
                </div>
              </div>
              
              {/* Top Referrers */}
              {growthData.referral_activity.top_referrers.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    Top Referrers
                  </h4>
                  <div className="bg-white dark:bg-gray-800 rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Rank</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Code</TableHead>
                          <TableHead className="text-right">Referrals</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {growthData.referral_activity.top_referrers.map((ref, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                            </TableCell>
                            <TableCell>{ref.name}</TableCell>
                            <TableCell className="font-mono text-sm">{ref.referral_code}</TableCell>
                            <TableCell className="text-right font-bold">{ref.referral_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              
              {/* Campaign Send Status */}
              {growthData.campaign_sends.last_send && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <p className="text-green-700 dark:text-green-400 text-sm">
                    <strong>Last Campaign Sent:</strong> {new Date(growthData.campaign_sends.last_send).toLocaleString()}<br/>
                    <strong>SMS Sent:</strong> {growthData.campaign_sends.sms_sent} | <strong>Email Sent:</strong> {growthData.campaign_sends.email_sent}<br/>
                    <strong>By:</strong> {growthData.campaign_sends.sent_by}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_members}</p>
                  <p className="text-xs text-muted-foreground">Total Members</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_referrals}</p>
                  <p className="text-xs text-muted-foreground">Total Referrals</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.coupons?.total_issued || 0}</p>
                  <p className="text-xs text-muted-foreground">Coupons Issued</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Percent className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.coupons?.redemption_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Redemption Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Coupon Breakdown */}
      {stats?.coupons && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gift className="w-5 h-5" />
              Coupon Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.coupons.used}</p>
                <p className="text-xs text-muted-foreground">Used</p>
              </div>
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.coupons.active}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.coupons.expired_unused}</p>
                <p className="text-xs text-muted-foreground">Expired Unused</p>
              </div>
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-2xl font-bold">{stats.coupons.total_issued}</p>
                <p className="text-xs text-muted-foreground">Total Issued</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Referral Link Activity - Who's Actually Sharing */}
      {activity && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MousePointer className="w-5 h-5 text-indigo-500" />
              Referral Link Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Activity Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{activity.summary?.active_sharers || 0}</p>
                <p className="text-xs text-muted-foreground">Active Sharers</p>
              </div>
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-2xl font-bold">{activity.summary?.inactive_sharers || 0}</p>
                <p className="text-xs text-muted-foreground">Not Sharing Yet</p>
              </div>
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{activity.summary?.total_clicks || 0}</p>
                <p className="text-xs text-muted-foreground">Total Link Clicks</p>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{activity.summary?.overall_conversion_rate || 0}%</p>
                <p className="text-xs text-muted-foreground">Click → Register Rate</p>
              </div>
            </div>

            {/* Active Sharers Table */}
            {activity.active_sharers && activity.active_sharers.length > 0 && (
              <>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-green-500" />
                  Members Actively Sharing (Link Clicks Tracked)
                </h4>
                <div className="overflow-x-auto mb-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead className="text-center">Clicks</TableHead>
                        <TableHead className="text-center">Registrations</TableHead>
                        <TableHead className="text-center">Conv. Rate</TableHead>
                        <TableHead>Last Click</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activity.active_sharers.slice(0, 20).map((member) => (
                        <TableRow key={member.user_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{member.name}</p>
                              <p className="text-xs text-muted-foreground">{member.email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{member.referral_code}</TableCell>
                          <TableCell className="text-center font-bold text-blue-600 dark:text-blue-400">
                            {member.referral_clicks || 0}
                          </TableCell>
                          <TableCell className="text-center font-bold text-green-600 dark:text-green-400">
                            {member.successful_referrals || 0}
                          </TableCell>
                          <TableCell className="text-center">
                            {member.conversion_rate ? `${member.conversion_rate.toFixed(1)}%` : '0%'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {member.last_click_at ? new Date(member.last_click_at).toLocaleDateString() : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}

            {/* Inactive Notice */}
            {activity.inactive_count > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="text-amber-700 dark:text-amber-400 text-sm">
                  <strong>{activity.inactive_count} members</strong> have not shared their referral link yet. 
                  Consider sending a reminder email/SMS to encourage them to start sharing!
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* SMS Campaign Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            SMS Campaign Controls
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-700 dark:text-amber-400">SMS Campaigns Ready But Paused</p>
                <p className="text-sm text-amber-600 dark:text-amber-500">
                  SMS campaigns will not send until you explicitly click &quot;Send for Real&quot;. 
                  Use &quot;Preview&quot; to see what would be sent first.
                </p>
              </div>
            </div>
          </div>

          {smsQueue && (
            <div className="grid md:grid-cols-2 gap-4">
              {/* Coupon Earned SMS */}
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Gift className="w-5 h-5 text-primary" />
                  <h4 className="font-medium">Coupon Earned SMS</h4>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Send &quot;You earned X% off&quot; messages to users who haven&apos;t been notified yet.
                </p>
                <p className="text-lg font-bold mb-4">
                  {smsQueue.pending_coupon_earned_sms?.length || 0} pending
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSendCampaign('coupon_earned', true)}
                    disabled={sendingCampaign || !smsQueue.pending_coupon_earned_sms?.length}
                  >
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleSendCampaign('coupon_earned', false)}
                    disabled={sendingCampaign || !smsQueue.pending_coupon_earned_sms?.length}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Send for Real
                  </Button>
                </div>
              </div>

              {/* Nudge SMS */}
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <h4 className="font-medium">Expiry Nudge SMS</h4>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Send &quot;Your coupon expires in X days&quot; reminders.
                </p>
                <p className="text-lg font-bold mb-4">
                  {smsQueue.pending_nudge_sms?.length || 0} pending
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSendCampaign('nudge', true)}
                    disabled={sendingCampaign || !smsQueue.pending_nudge_sms?.length}
                  >
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleSendCampaign('nudge', false)}
                    disabled={sendingCampaign || !smsQueue.pending_nudge_sms?.length}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Send for Real
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Message Templates Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-500" />
            Message Templates Preview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* SMS Templates */}
          <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Send className="w-4 h-4 text-green-500" />
              SMS Message Templates
            </h4>
            <div className="space-y-3">
              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1 font-medium">Coupon Earned SMS:</p>
                <p className="text-sm font-mono bg-background p-3 rounded border">
                  Vinaka [Name]! You earned [X]% off at Bulaccino Cafe! Your code: [CODE]. Expires in [N] days. Login to see your rewards: bulaccinocafe.com/my-account - enter your phone number to get a code via SMS!
                </p>
              </div>
              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1 font-medium">Expiry Reminder SMS:</p>
                <p className="text-sm font-mono bg-background p-3 rounded border">
                  Reminder [Name]! Your [X]% off coupon expires in [N] days! Code: [CODE]. Don&apos;t miss out! Login: bulaccinocafe.com/my-account (enter your phone, get SMS code)
                </p>
              </div>
            </div>
          </div>

          {/* Email Templates */}
          <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Gift className="w-4 h-4 text-purple-500" />
              Email Campaign Templates
            </h4>
            <div className="space-y-3">
              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-muted-foreground font-medium">Referral Launch Email</p>
                  <a 
                    href="/email_templates/referral_launch_email.html" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline"
                  >
                    View Full Template →
                  </a>
                </div>
                <p className="text-sm text-muted-foreground">
                  Subject: &quot;Refer Friends & Earn Rewards - Bulaccino Cafe&quot;
                </p>
                <p className="text-sm mt-2">
                  Includes: Referral tier rewards table, CTA to login at /my-account, Coffee Pass announcement (Monday, June 15th), location reminder
                </p>
              </div>
              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-muted-foreground font-medium">Coffee Pass Launch Email</p>
                  <a 
                    href="/email_templates/coffee_pass_launch_june15.html" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline"
                  >
                    View Full Template →
                  </a>
                </div>
                <p className="text-sm text-muted-foreground">
                  Subject: &quot;Bula Coffee Pass NOW ON SALE - Bulaccino Cafe&quot;
                </p>
                <p className="text-sm mt-2">
                  Includes: $95 price, 365 days of daily coffee, urgency (500 passes only), directions to Namaka location
                </p>
              </div>
            </div>
          </div>

          {/* Login Instructions Note */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-blue-700 dark:text-blue-400 text-sm">
              <strong>Login Flow:</strong> All messages direct users to <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">bulaccinocafe.com/my-account</code> where they enter their phone number and receive a verification code via SMS. This is optimized for Fiji locals who prefer SMS over data.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Top Referrers */}
      {stats?.top_referrers && stats.top_referrers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Top Referrers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rank</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Referrals</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.top_referrers.map((referrer, idx) => (
                  <TableRow key={referrer.user_id}>
                    <TableCell>
                      <span className={`font-bold ${idx < 3 ? 'text-amber-500' : ''}`}>
                        #{idx + 1}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">{referrer.name}</TableCell>
                    <TableCell className="text-muted-foreground">{referrer.email}</TableCell>
                    <TableCell className="text-right font-bold">{referrer.referral_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Coupon Report */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Coupon Report
            </CardTitle>
            <div className="flex gap-2">
              {['all', 'active', 'used', 'expired'].map((filter) => (
                <Button
                  key={filter}
                  variant={couponFilter === filter ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCouponFilter(filter)}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {couponReport?.coupons?.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expires</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {couponReport.coupons.map((coupon) => {
                    const isExpired = new Date(coupon.expires_at) < new Date();
                    const status = coupon.is_used ? 'used' : isExpired ? 'expired' : 'active';
                    
                    return (
                      <TableRow key={coupon.code}>
                        <TableCell className="font-mono text-sm">{coupon.code}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{coupon.user_name}</p>
                            <p className="text-xs text-muted-foreground">{coupon.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-bold text-primary">{coupon.discount_percent}%</TableCell>
                        <TableCell>{coupon.tier_earned} referrals</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            status === 'used' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            status === 'expired' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                            'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                          }`}>
                            {status}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(coupon.expires_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No coupons found</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
